import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { georef, type GeorefMap } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { validateUploadSize, type UploadProgress } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';

/**
 * Moduł „Dopasowanie map": lista dopasowanych (georeferowanych) rastrów + wgranie nowej mapy.
 *
 * Wgranie od razu uruchamia automat po stronie serwera — użytkownik ląduje w edytorze na gotowej
 * propozycji (wycięte pole mapy + punkty kontrolne + transformacja), a nie na pustym ekranie.
 */
export function GeorefPage() {
  const nav = useNavigate();
  const [mapy, setMapy] = useState<GeorefMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [postep, setPostep] = useState<UploadProgress | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [nadDropem, setNadDropem] = useState(false);

  const odswiez = () => georef.list().then(setMapy).catch(e => setErr(String(e))).finally(() => setLoading(false));
  useEffect(() => { odswiez(); }, []);

  const wgraj = (f: File) => {
    const problem = validateUploadSize(f);
    if (problem) { setErr(problem); return; }
    setBusy(true); setErr(null); setPostep(null);
    georef.upload(f, { name: f.name.replace(/\.[^.]+$/, '') }, setPostep)
      .then(m => nav(`/dopasowanie-map/${m.id}`))
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => { setBusy(false); setPostep(null); });
  };

  const pill = (m: GeorefMap) =>
    m.status === 'APPROVED' ? 'pill pill-green'
      : m.status === 'PROPOSED' ? 'pill pill-amber'
        : m.status === 'REJECTED' ? 'pill pill-red' : 'pill pill-gray';

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Dopasowanie map</h1>
          <div className="text-ink-400 text-[13px]">
            Georeferencja rastrów (JPG · PNG · PDF): automatyczne wycięcie pola mapy i dopasowanie
            współrzędnych → weryfikacja i zatwierdzenie przez człowieka → warstwa na mapie
          </div>
        </div>
      </header>

      {err && <div className="text-red-600 text-[12px] mb-3 p-2 bg-red-50 border border-red-200 rounded">{err}</div>}

      <label
        onDragOver={e => { e.preventDefault(); setNadDropem(true); }}
        onDragLeave={() => setNadDropem(false)}
        onDrop={e => {
          e.preventDefault(); setNadDropem(false);
          const f = e.dataTransfer.files?.[0];
          if (f) wgraj(f);
        }}
        className={'block mb-5 rounded-[10px] border-2 border-dashed p-6 text-center cursor-pointer transition '
          + (nadDropem ? 'border-water-500 bg-water-50/60' : 'border-ink-200 bg-white hover:border-ink-400')}
      >
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,application/pdf"
          className="hidden"
          disabled={busy}
          onChange={e => { const f = e.target.files?.[0]; if (f) wgraj(f); e.currentTarget.value = ''; }}
        />
        <Icon name="upload" className="w-6 h-6 mx-auto text-ink-400 mb-2" />
        <div className="text-[13px] font-semibold text-ink-800">
          {busy ? 'Wgrywam i dopasowuję…' : 'Przeciągnij mapę albo kliknij, aby wybrać plik'}
        </div>
        <div className="text-[11px] text-ink-400 mt-1">
          JPG, PNG, WebP lub PDF (wielostronicowy — stronę wybierzesz w edytorze). Do 60 MB.
        </div>
        {postep && (
          <div className="mt-3 max-w-[420px] mx-auto">
            <UploadProgressBar percent={postep.percent} loadedBytes={postep.loaded} totalBytes={postep.total} compact />
          </div>
        )}
      </label>

      {loading ? (
        <div className="text-ink-400 text-[13px]">Ładowanie…</div>
      ) : mapy.length === 0 ? (
        <div className="card"><div className="card-body text-center text-ink-400 text-[13px] py-10">
          Nie ma jeszcze żadnej dopasowanej mapy. Wgraj pierwszą — resztę spróbuje zrobić program.
        </div></div>
      ) : (
        <div className="card">
          <div className="card-body !p-0">
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Mapa</th>
                  <th>Status</th>
                  <th>Układ</th>
                  <th className="text-right">Błąd dopasowania</th>
                  <th className="text-right">Skala</th>
                  <th>Punkty</th>
                  <th>Warstwa</th>
                </tr>
              </thead>
              <tbody>
                {mapy.map(m => (
                  <tr key={m.id} onClick={() => nav(`/dopasowanie-map/${m.id}`)}>
                    <td>
                      <div className="font-semibold text-ink-900 text-[13px]">{m.name}</div>
                      <div className="font-mono text-[10px] text-ink-400 truncate max-w-[280px]">
                        {m.sourceFilename}
                        {m.pageCount && m.pageCount > 1 ? ` · str. ${m.pageNumber}/${m.pageCount}` : ''}
                      </div>
                    </td>
                    <td><span className={pill(m)}>{m.statusLabel}</span></td>
                    <td className="text-[12px]">{m.mapCrsLabel ?? <span className="text-ink-300">—</span>}</td>
                    <td className="text-right font-mono tabular-nums text-[12px]">
                      {m.rmsM == null ? <span className="text-ink-300">—</span>
                        : !m.rmsMiarodajny ? <span className="text-amber-600" title="Dokładnie 3 punkty — RMS zerowy z definicji">niemiarodajny</span>
                          : <span className={m.rmsM > 25 ? 'text-amber-600' : 'text-emerald-600'}>{m.rmsM.toFixed(1)} m</span>}
                    </td>
                    <td className="text-right font-mono tabular-nums text-[12px]">
                      {m.skalaMPx ? `${m.skalaMPx.toFixed(2)} m/px` : <span className="text-ink-300">—</span>}
                    </td>
                    <td className="text-[12px]">{m.gcps.filter(g => g.active).length}/{m.gcps.length}</td>
                    <td className="font-mono text-[10px] text-ink-500">
                      {m.layerCode ?? <span className="text-ink-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
